import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css'; // Use the same or updated CSS file
import SidebarLayout from './SidebarLayout';

const Admin = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [selectedPermission, setSelectedPermission] = useState({});
    const [loginAfterDate, setLoginAfterDate] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });

    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${apiUrl}/admin`, { withCredentials: true });

                // Sort by: approved users first, then alphabetically by username
                const sortedUsers = response.data.users.sort((a, b) => {
                    // Put approved users first
                    if (a.is_approved !== b.is_approved) {
                        return b.is_approved - a.is_approved; // true (1) comes before false (0)
                    }

                    // If both have same approval status, sort alphabetically by username
                    const nameA = a.username.toLowerCase();
                    const nameB = b.username.toLowerCase();
                    return nameA.localeCompare(nameB);
                });

                setUsers(sortedUsers);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    navigate('/login');
                } else {
                    console.error('Error fetching users:', error);
                }
            }
        };


        const fetchPermissions = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/permissions`, { withCredentials: true });
                setPermissions(response.data.permissions);
            } catch (error) {
                console.error('Error fetching permissions:', error);
            }
        };

        fetchUsers();
        fetchPermissions();
    }, [navigate]);

    const toggleApproval = async (userId, currentStatus) => {
        try {
            await axios.post(`${apiUrl}/approve/${userId}`, 
            { is_approved: !currentStatus },
            { withCredentials: true });

            setUsers(users.map(user => 
                user.id === userId ? { ...user, is_approved: !currentStatus } : user
            ));
        } catch (error) {
            console.error('Error updating approval state:', error);
        }
    };

    const grantPermission = async (userId) => {
        try {
            const permissionToAssign = selectedPermission[userId];
            if (!permissionToAssign) {
                alert('Please select a permission first.');
                return;
            }

            const response = await axios.post(`${apiUrl}/api/assign_permission`,
                { user_id: userId, permissions: [permissionToAssign] },
                { withCredentials: true }
            );

            alert(`Permission granted: ${response.data.assigned_permissions.join(', ')}`);

            // Optionally update state to show new permission immediately:
            setUsers(users.map(user =>
                user.id === userId
                    ? { ...user, permissions: [...user.permissions, permissionToAssign] }
                    : user
            ));
        } catch (error) {
            console.error('Error granting permission:', error);
            alert('Failed to grant permission.');
        }
    };

    const removePermission = async (userId, permission, username) => {
        const confirmRemove = window.confirm(`Are you sure you want to remove the "${permission}" permission from user "${username}"?`);

        if (!confirmRemove) return;

        try {
            await axios.post(`${apiUrl}/api/remove_permission`,
                { user_id: userId, permission },
                { withCredentials: true }
            );

            setUsers(users.map(user =>
                user.id === userId
                    ? {
                        ...user,
                        permissions: user.permissions.filter(p => p !== permission)
                    }
                    : user
            ));
        } catch (error) {
            console.error('Error removing permission:', error);
            alert('Failed to remove permission.');
        }
    };

    const sortedUsers = [...users].sort((a, b) => {
        const { key, direction } = sortConfig;

        let aValue = a[key];
        let bValue = b[key];

        // Normalize values for sorting
        if (key === 'username') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        } else if (key === 'last_login') {
            aValue = aValue ? new Date(aValue) : new Date(0);
            bValue = bValue ? new Date(bValue) : new Date(0);
        } else if (key === 'is_approved') {
            aValue = aValue ? 1 : 0;
            bValue = bValue ? 1 : 0;
        }

        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
    });

    const filteredUsers = sortedUsers.filter(user => {
        const matchesSearch =
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDate = !loginAfterDate || (
            user.last_login &&
            new Date(user.last_login) >= new Date(loginAfterDate)
        );

        return matchesSearch && matchesDate;
    });

    const handleSort = (key) => {
        setSortConfig(prevConfig => {
            if (prevConfig.key === key) {
                // Toggle direction
                return {
                    key,
                    direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
                };
            } else {
                // New key, default to ascending
                return { key, direction: 'asc' };
            }
        });
    };

    return (
        <div className="admin-panel">
            <h1>Admin Panel</h1>
            <h2>All Users</h2>
            <input 
                type="text" 
                placeholder="Search by username or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
            />
            <div className="filter-group">
                <label htmlFor="login-after-filter" className="filter-label">
                    Filter by Last Login Date:
                </label>
                <input
                    id="login-after-filter"
                    type="date"
                    value={loginAfterDate}
                    onChange={(e) => setLoginAfterDate(e.target.value)}
                    className="date-filter-input"
                />
            </div>
            <div className="table-responsive">
                <table className="user-table">
                    <thead>
                        <tr>
                            <th className="sticky-col" onClick={() => handleSort('username')} style={{ cursor: 'pointer' }}>
                                Username {sortConfig.key === 'username' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th>Email</th>
                            <th onClick={() => handleSort('is_approved')} style={{ cursor: 'pointer' }}>
                                Status {sortConfig.key === 'is_approved' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th>Action</th>
                            <th>Current Permissions</th>
                            <th>Assign New Permission</th>
                            <th onClick={() => handleSort('last_login')} style={{ cursor: 'pointer' }}>
                                Last Login {sortConfig.key === 'last_login' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th>Last Login IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map(user => (
                                <tr key={user.id}>
                                    <td className="sticky-col">{user.username}</td>
                                    <td className="email-cell">{user.email}</td>
                                    <td>
                                        <span>{user.is_approved ? 'Approved' : 'Pending'}</span>
                                    </td>
                                    <td>
                                        <button 
                                            onClick={() => toggleApproval(user.id, user.is_approved)} 
                                            className="approve-button"
                                        >
                                            {user.is_approved ? 'Revoke Approval' : 'Approve'}
                                        </button>
                                    </td>
                                    <td>
                                        {user.permissions && user.permissions.length > 0 ? (
                                            <ul className="permission-list">
                                                {user.permissions.map((perm, index) => (
                                                    <li key={index} className="permission-item">
                                                        {perm}
                                                        <button
                                                            onClick={() => removePermission(user.id, perm, user.username)}
                                                            className="remove-permission-button"
                                                            title="Remove permission"
                                                        >
                                                            ❌
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span>None</span>
                                        )}
                                    </td>
                                    <td>
                                        <select
                                            value={selectedPermission[user.id] || ''}
                                            onChange={(e) =>
                                                setSelectedPermission({
                                                    ...selectedPermission,
                                                    [user.id]: e.target.value
                                                })
                                            }
                                        >
                                            <option value="">Select Permission</option>
                                            {permissions.map((perm) => (
                                                <option key={perm} value={perm}>{perm}</option>
                                            ))}
                                        </select>
                                        <button 
                                            onClick={() => grantPermission(user.id)} 
                                            className="permission-button"
                                        >
                                            Grant
                                        </button>
                                    </td>
                                    <td>
                                        {user.last_login
                                            ? new Date(user.last_login).toLocaleString()
                                            : 'Never'}
                                    </td>
                                    <td>{user.last_login_ip}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="no-users">No users found.</td>
                            </tr>
                        )}
                    </tbody>

                </table>
            </div>
        </div>
    );
};

export default Admin;
